// =============================================================================
// PEDRA — src/components/SheetDiagram.tsx
// Desenho SVG de UMA chapa: retangulos das pecas, sobras hachuradas, linhas de
// corte, cotas das dimensoes e fitas de borda. Zoom por roda e pan por arrasto.
//
// Sistema de coordenadas: identico ao do dominio (origem no canto superior
// esquerdo, X = largura, Y = comprimento), porem o desenho acontece em
// MILIMETROS, nao em Micron.
//
// POR QUE: o Chrome limita `font-size` a 10000px. Com o viewBox em Micron uma
// chapa de 2750 mm virava "0 0 1830000 2750000" e a fonte proporcional caia em
// ~52.884 unidades de usuario — acima do teto. TODO texto era capado em 10000px
// e renderizava com ~3px de altura na tela. Dividindo por 1000 o viewBox fica
// "0 0 1830 2750" e a fonte ~52.9, folgadissimo abaixo do limite.
//
// CONVENCAO DESTE ARQUIVO:
//   - "valor de dominio"      -> Micron, vem das props, so serve para FORMATAR
//                                texto (formatDimensions, shortDims, ...).
//   - "coordenada de desenho" -> milimetros, obtida por `draw()`, e a unica
//                                coisa que entra em x/y/width/height/fontSize.
// Nunca misture os dois: formatar um valor ja convertido daria numero errado.
// =============================================================================

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  ReactNode,
} from 'react';
import type {
  CutInstruction,
  EdgeBandType,
  FreeArea,
  Id,
  Micron,
  Placement,
  Settings,
  SheetLayout,
} from '../core/types';
import { formatDimensions, fromMicronTrim } from '../core/units';

// -----------------------------------------------------------------------------
// PROPS
// -----------------------------------------------------------------------------

export interface SheetDiagramProps {
  sheet: SheetLayout;
  settings: Settings;
  showLabels: boolean;
  showDimensions: boolean;
  showCuts: boolean;
  showEdgeBanding: boolean;
  colorFilled: boolean;
  edgeBands: readonly EdgeBandType[];
  /** Largura util em px para escalar o SVG. */
  maxWidth: number;
  highlightedPartId?: Id | null;
  highlightedCutSeq?: number | null;
  onHoverPart?(partId: Id | null): void;
  onClickPart?(partId: Id): void;
}

/**
 * Zoom/pan interno, em unidades do viewBox (milimetros).
 * `scale` e adimensional, entao MIN/MAX_SCALE nao dependem da unidade.
 */
interface ViewTransform {
  scale: number;
  tx: number;
  ty: number;
}

const IDENTITY: ViewTransform = { scale: 1, tx: 0, ty: 0 };
const MIN_SCALE = 0.4;
const MAX_SCALE = 32;
/** Deslocamento minimo (px) para o arrasto virar pan e cancelar o clique. */
const DRAG_THRESHOLD = 3;

/** Um dedo / ponteiro encostado no diagrama, em pixels de tela. */
interface PointerPos {
  x: number;
  y: number;
}

/** Distancia e ponto medio entre dois dedos — o estado de uma pinca. */
interface PinchState {
  dist: number;
  cx: number;
  cy: number;
}

/**
 * Le a pinca a partir dos DOIS PRIMEIROS ponteiros encostados. Um terceiro dedo
 * e ignorado de proposito: trocar de par no meio do gesto faria a chapa saltar.
 */
function readPinch(pointers: ReadonlyMap<number, PointerPos>): PinchState | null {
  const [a, b] = [...pointers.values()];
  if (a === undefined || b === undefined) return null;
  return {
    // Piso de 1px: o divisor da razao de escala nunca pode ser zero.
    dist: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)),
    cx: (a.x + b.x) / 2,
    cy: (a.y + b.y) / 2,
  };
}

/** Micron por milimetro — fator unico de conversao dominio -> desenho. */
const MICRON_PER_MM = 1000;

/**
 * Converte um valor de dominio (Micron) em coordenada de desenho (mm).
 * Use SEMPRE que o numero for parar em x/y/width/height/fontSize/r do SVG.
 * NUNCA use antes de formatar texto — as funcoes de formatacao esperam Micron.
 */
function draw(value: Micron): number {
  return value / MICRON_PER_MM;
}

// -----------------------------------------------------------------------------
// HELPERS
// -----------------------------------------------------------------------------

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Largura aproximada de um texto em unidades do SVG (fonte de largura media). */
function textWidth(text: string, fontSize: number): number {
  return text.length * fontSize * 0.56;
}

/**
 * Trunca o texto para caber em `maxWidth`, acrescentando reticencias.
 * Devolve `null` quando nem duas letras cabem — nesse caso nao desenhamos nada.
 */
function fitText(text: string, maxWidth: number, fontSize: number): string | null {
  if (!text) return null;
  const charW = fontSize * 0.56;
  const maxChars = Math.floor(maxWidth / charW);
  if (maxChars < 2) return null;
  if (text.length <= maxChars) return text;
  if (maxChars < 4) return null;
  return `${text.slice(0, maxChars - 1)}…`;
}

/**
 * Dimensoes curtas para caber dentro da peca: "600 × 720" (sem sufixo).
 *
 * `fromMicronTrim` e nao `fromMicron`: aqui `decimalPlaces` e o TETO de casas.
 * Dentro de um retangulo de 300 mm de lado nao ha espaco para zero decorativo —
 * num projeto em metro (3 casas) o rotulo sairia "0,300 × 0,300" e o texto
 * encolheria ou seria truncado para caber. "0,3 × 0,3" diz o mesmo em 6 letras
 * a menos, e e exatamente o que o site original mostra.
 */
function shortDims(length: Micron, width: Micron, settings: Settings): string {
  const a = settings.dimensionOrder === 'widthFirst' ? width : length;
  const b = settings.dimensionOrder === 'widthFirst' ? length : width;
  return `${fromMicronTrim(a, settings.unit, settings.decimalPlaces)} × ${fromMicronTrim(
    b,
    settings.unit,
    settings.decimalPlaces,
  )}`;
}

/** Encontra o tipo de fita pelo id (lista pequena, busca linear serve). */
function findBand(bands: readonly EdgeBandType[], id: Id | null): EdgeBandType | null {
  if (!id) return null;
  for (const b of bands) if (b.id === id) return b;
  return null;
}

/** Sanitiza o `useId` do React para uso em `url(#...)`. */
function safeId(raw: string): string {
  return raw.replace(/[^a-zA-Z0-9_-]/g, '');
}

// -----------------------------------------------------------------------------
// COTA TECNICA (dimension line)
// -----------------------------------------------------------------------------

interface DimLineProps {
  orientation: 'h' | 'v';
  /** Coordenada de desenho fixa, em mm (y para horizontal, x para vertical). */
  at: number;
  /** Inicio da cota, em mm de desenho. */
  from: number;
  /** Fim da cota, em mm de desenho. */
  to: number;
  /** Texto ja formatado a partir do valor de dominio (Micron). */
  text: string;
  fontSize: number;
  /** Meio comprimento das marcas de extremidade, em mm de desenho. */
  tick: number;
}

function DimLine(props: DimLineProps): ReactElement {
  const { orientation, at, from, to, text, fontSize, tick } = props;
  const mid = (from + to) / 2;
  const tw = textWidth(text, fontSize);
  const pad = fontSize * 0.35;

  if (orientation === 'h') {
    return (
      <g className="diagram-dim">
        <line className="diagram-dim-line" x1={from} y1={at} x2={to} y2={at} />
        <line className="diagram-dim-tick" x1={from} y1={at - tick} x2={from} y2={at + tick} />
        <line className="diagram-dim-tick" x1={to} y1={at - tick} x2={to} y2={at + tick} />
        <rect
          className="diagram-dim-bg"
          x={mid - tw / 2 - pad}
          y={at - fontSize * 0.62 - pad / 2}
          width={tw + pad * 2}
          height={fontSize * 1.24 + pad}
          rx={pad}
        />
        <text
          className="diagram-dim-text"
          x={mid}
          y={at}
          fontSize={fontSize}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {text}
        </text>
      </g>
    );
  }

  return (
    <g className="diagram-dim">
      <line className="diagram-dim-line" x1={at} y1={from} x2={at} y2={to} />
      <line className="diagram-dim-tick" x1={at - tick} y1={from} x2={at + tick} y2={from} />
      <line className="diagram-dim-tick" x1={at - tick} y1={to} x2={at + tick} y2={to} />
      <g transform={`rotate(-90 ${at} ${mid})`}>
        <rect
          className="diagram-dim-bg"
          x={at - tw / 2 - pad}
          y={mid - fontSize * 0.62 - pad / 2}
          width={tw + pad * 2}
          height={fontSize * 1.24 + pad}
          rx={pad}
        />
        <text
          className="diagram-dim-text"
          x={at}
          y={mid}
          fontSize={fontSize}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {text}
        </text>
      </g>
    </g>
  );
}

// -----------------------------------------------------------------------------
// SOBRAS
// -----------------------------------------------------------------------------

interface OffcutLayerProps {
  offcuts: readonly FreeArea[];
  wasteFill: string;
  offcutFill: string;
  showLabels: boolean;
  settings: Settings;
  /** Fonte de referencia, em mm de desenho. */
  baseFont: number;
}

/**
 * Menor fonte ainda legivel numa sobra, como fracao de `baseFont`.
 * Abaixo disso o rotulo nao e encolhido: ele some (ver `OffcutLayer`).
 */
const OFFCUT_MIN_FONT_RATIO = 0.52;

function OffcutLayer(props: OffcutLayerProps): ReactElement {
  const { offcuts, wasteFill, offcutFill, showLabels, settings, baseFont } = props;
  return (
    <g className="diagram-offcuts" aria-hidden="true">
      {offcuts.map((o) => {
        // Geometria em mm; o texto continua saindo dos valores em Micron.
        const x = draw(o.x);
        const y = draw(o.y);
        const w = draw(o.width);
        const h = draw(o.length);
        const dims = shortDims(o.length, o.width, settings);

        // A fonte SO pode encolher: `Math.min`, nunca `clamp` com piso.
        //
        // POR QUE: com piso a fonte era forcada a `baseFont * 0.5` mesmo numa
        // faixa de sobra de 70 mm de altura. O texto ficava mais alto que a
        // propria faixa, invadia as faixas vizinhas e a base da chapa virava um
        // borrao de rotulos sobrepostos. Uma medida ilegivel nao informa menos
        // que nenhuma: informa ERRADO, porque parece um numero e nao e.
        // Entao quando nao cabe com folga o rotulo simplesmente nao e desenhado
        // — a medida continua acessivel no <title> (tooltip do mouse).
        const fs = Math.min(baseFont, w / 9, h / 2.8);
        const fits = fs >= baseFont * OFFCUT_MIN_FONT_RATIO && h >= fs * 2.2;
        const label = showLabels && fits ? fitText(dims, w * 0.86, fs) : null;
        return (
          <g key={o.id} className={o.usable ? 'diagram-offcut is-usable' : 'diagram-offcut'}>
            <rect x={x} y={y} width={w} height={h} fill={o.usable ? offcutFill : wasteFill} />
            <rect className="diagram-offcut-border" x={x} y={y} width={w} height={h} fill="none" />
            {label ? (
              <text
                className="diagram-offcut-text"
                x={x + w / 2}
                y={y + h / 2}
                fontSize={fs}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {label}
              </text>
            ) : null}
            <title>{`${o.usable ? 'Sobra aproveitável' : 'Retalho'} — ${dims}`}</title>
          </g>
        );
      })}
    </g>
  );
}

// -----------------------------------------------------------------------------
// UMA PECA
// -----------------------------------------------------------------------------

interface PartRectProps {
  placement: Placement;
  index: number;
  colorFilled: boolean;
  showLabels: boolean;
  showEdgeBanding: boolean;
  edgeBands: readonly EdgeBandType[];
  settings: Settings;
  /** Fonte de referencia, em mm de desenho. */
  baseFont: number;
  /** Espessura minima visivel da fita, em mm de desenho. */
  bandMin: number;
  highlighted: boolean;
  onHover?(partId: Id | null): void;
  onClick?(partId: Id): void;
}

function PartRect(props: PartRectProps): ReactElement {
  const {
    placement: p,
    index,
    colorFilled,
    showLabels,
    showEdgeBanding,
    edgeBands,
    settings,
    baseFont,
    bandMin,
    highlighted,
    onHover,
    onClick,
  } = props;

  // Retangulo em coordenadas de desenho (mm). Os valores de dominio (p.length,
  // p.width em Micron) seguem intactos para a formatacao do texto.
  const x = draw(p.x);
  const y = draw(p.y);
  const w = draw(p.width);
  const h = draw(p.length);
  const cx = x + w / 2;
  const cy = y + h / 2;

  // Fonte adaptada ao retangulo: nunca maior que a base, nunca ilegivel.
  const fs = clamp(Math.min(w / 8, h / 3.2), baseFont * 0.42, baseFont);
  const innerW = w * 0.92;
  const dims = shortDims(p.length, p.width, settings);

  // Rotulo em niveis: nome + medidas -> so nome -> so medidas -> indice -> nada.
  let line1: string | null = null;
  let line2: string | null = null;
  if (showLabels) {
    const twoLines = h >= fs * 2.6;
    const nameFit = p.label ? fitText(p.label, innerW, fs) : null;
    const dimsFit = fitText(dims, innerW, fs * 0.88);
    if (twoLines && nameFit && dimsFit) {
      line1 = nameFit;
      line2 = dimsFit;
    } else if (nameFit) {
      line1 = nameFit;
    } else if (dimsFit) {
      line1 = dimsFit;
    } else {
      line1 = fitText(String(index + 1), innerW, fs);
    }
  } else {
    line1 = fitText(dims, innerW, fs);
  }

  // Fitas de borda: faixa grossa desenhada por dentro do lado correspondente.
  const bands: ReactNode[] = [];
  if (showEdgeBanding) {
    const sides: ReadonlyArray<['top' | 'right' | 'bottom' | 'left', Id | null]> = [
      ['top', p.edgeBanding.top],
      ['right', p.edgeBanding.right],
      ['bottom', p.edgeBanding.bottom],
      ['left', p.edgeBanding.left],
    ];
    for (const [side, id] of sides) {
      const band = findBand(edgeBands, id);
      if (!band) continue;
      // `band.thickness` e valor de dominio (Micron): converte antes de comparar
      // com `bandMin`, que ja esta em mm de desenho.
      const t = Math.max(draw(band.thickness), bandMin);
      const key = `${p.partId}-${p.instance}-${side}`;
      if (side === 'top') {
        bands.push(
          <rect key={key} className="diagram-band" x={x} y={y} width={w} height={t} fill={band.color}>
            <title>{`Fita ${band.name} — topo`}</title>
          </rect>,
        );
      } else if (side === 'bottom') {
        bands.push(
          <rect
            key={key}
            className="diagram-band"
            x={x}
            y={y + h - t}
            width={w}
            height={t}
            fill={band.color}
          >
            <title>{`Fita ${band.name} — base`}</title>
          </rect>,
        );
      } else if (side === 'left') {
        bands.push(
          <rect key={key} className="diagram-band" x={x} y={y} width={t} height={h} fill={band.color}>
            <title>{`Fita ${band.name} — esquerda`}</title>
          </rect>,
        );
      } else {
        bands.push(
          <rect
            key={key}
            className="diagram-band"
            x={x + w - t}
            y={y}
            width={t}
            height={h}
            fill={band.color}
          >
            <title>{`Fita ${band.name} — direita`}</title>
          </rect>,
        );
      }
    }
  }

  const glyph = fs * 0.9;
  const showGlyph = p.rotated && w > glyph * 2.4 && h > glyph * 2.4;

  return (
    <g
      className={highlighted ? 'diagram-part is-highlighted' : 'diagram-part'}
      data-part-id={p.partId}
      data-instance={p.instance}
      onMouseEnter={onHover ? () => onHover(p.partId) : undefined}
      onClick={onClick ? () => onClick(p.partId) : undefined}
    >
      <rect
        className="diagram-part-rect"
        x={x}
        y={y}
        width={w}
        height={h}
        fill={colorFilled ? p.color : 'none'}
      />
      {bands}
      {showGlyph ? (
        <text
          className="diagram-part-glyph"
          x={x + w - glyph * 0.7}
          y={y + glyph * 0.9}
          fontSize={glyph}
          textAnchor="middle"
          dominantBaseline="central"
        >
          ⟳
        </text>
      ) : null}
      {line1 && line2 ? (
        <>
          <text
            className="diagram-part-text"
            x={cx}
            y={cy - fs * 0.55}
            fontSize={fs}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {line1}
          </text>
          <text
            className="diagram-part-text is-dims"
            x={cx}
            y={cy + fs * 0.6}
            fontSize={fs * 0.88}
            textAnchor="middle"
            dominantBaseline="central"
          >
            {line2}
          </text>
        </>
      ) : line1 ? (
        <text
          className="diagram-part-text"
          x={cx}
          y={cy}
          fontSize={fs}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {line1}
        </text>
      ) : null}
      <title>{`${p.label || `Peça ${index + 1}`} — ${dims}${p.rotated ? ' (girada 90°)' : ''}`}</title>
    </g>
  );
}

// -----------------------------------------------------------------------------
// LINHAS DE CORTE
// -----------------------------------------------------------------------------

interface CutLayerProps {
  cuts: readonly CutInstruction[];
  highlightedCutSeq?: number | null;
  /** Fonte de referencia, em mm de desenho. */
  baseFont: number;
  showNumbers: boolean;
}

function CutLayer(props: CutLayerProps): ReactElement {
  const { cuts, highlightedCutSeq, baseFont, showNumbers } = props;
  /** Raio do badge, em mm de desenho (baseFont ja esta em mm). */
  const r = baseFont * 0.72;
  return (
    <g className="diagram-cuts" aria-hidden="true">
      {cuts.map((c) => {
        // c.pos, c.kerf, c.start e c.end sao Micron: viram mm para desenhar.
        const on = draw(c.pos + c.kerf / 2);
        const start = draw(c.start);
        const end = draw(c.end);
        const active = highlightedCutSeq === c.seq;
        const x1 = c.axis === 'x' ? on : start;
        const x2 = c.axis === 'x' ? on : end;
        const y1 = c.axis === 'x' ? start : on;
        const y2 = c.axis === 'x' ? end : on;
        const mx = (x1 + x2) / 2;
        const my = (y1 + y2) / 2;
        return (
          <g key={c.seq} className={active ? 'diagram-cut is-active' : 'diagram-cut'}>
            <line className="diagram-cut-line" x1={x1} y1={y1} x2={x2} y2={y2} />
            {showNumbers || active ? (
              <>
                <circle className="diagram-cut-badge" cx={mx} cy={my} r={r} />
                <text
                  className="diagram-cut-num"
                  x={mx}
                  y={my}
                  fontSize={r * 1.15}
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {c.seq}
                </text>
              </>
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

// -----------------------------------------------------------------------------
// COMPONENTE PRINCIPAL
// -----------------------------------------------------------------------------

/**
 * Mantem pelo menos ~20% da chapa dentro da area visivel.
 * `vbW`/`vbL` sao as dimensoes do viewBox (mm), as mesmas unidades de tx/ty.
 */
function clampView(v: ViewTransform, vbW: number, vbL: number): ViewTransform {
  const minTx = vbW * 0.2 - vbW * v.scale;
  const maxTx = vbW * 0.8;
  const minTy = vbL * 0.2 - vbL * v.scale;
  const maxTy = vbL * 0.8;
  return {
    scale: v.scale,
    tx: clamp(v.tx, Math.min(minTx, 0), Math.max(maxTx, 0)),
    ty: clamp(v.ty, Math.min(minTy, 0), Math.max(maxTy, 0)),
  };
}

export function SheetDiagram(props: SheetDiagramProps): ReactElement {
  const {
    sheet,
    settings,
    showLabels,
    showDimensions,
    showCuts,
    showEdgeBanding,
    colorFilled,
    edgeBands,
    maxWidth,
    highlightedPartId,
    highlightedCutSeq,
    onHoverPart,
    onClickPart,
  } = props;

  // viewBox em MILIMETROS (ver cabecalho): sem isso o Chrome capa as fontes.
  const vbW = Math.max(1, draw(sheet.width));
  const vbL = Math.max(1, draw(sheet.length));

  // Metricas de desenho derivadas do tamanho da chapa — tudo em mm, ja que
  // vbW/vbL sao mm. Numa chapa de 2750 mm baseFont fica em ~52.9 (antes: 52884).
  const big = Math.max(vbW, vbL);
  const small = Math.min(vbW, vbL);
  const baseFont = big / 52;
  const bandMin = small / 130;
  const hatch = big / 45;
  const dimFont = baseFont * 0.92;

  // MARGEM DO viewBox — a moldura onde as cotas sao desenhadas.
  //
  // POR QUE: as cotas ficavam DENTRO da chapa, e a etiqueta da cota vertical
  // (largura ~ `dimFont * 1.24` depois do `rotate(-90)`) estourava a borda
  // esquerda do viewBox — o SVG recorta no proprio viewBox, entao o texto saia
  // cortado ("3,07…" colado na borda). Reservando uma faixa em volta da chapa a
  // cota cabe inteira, o desenho fica com cara de prancha tecnica e a borda da
  // chapa (traco de 1,5 px) para de ser cortada ao meio.
  //
  // O 2,1 vem da geometria da etiqueta: a metade que sobra do centro da cota
  // (`margin / 2`) precisa cobrir `0,62 * fonte` de meia-altura mais `0,175 *
  // fonte` de respiro, ou seja `margin >= 1,59 * fonte`. 2,1 deixa folga.
  const margin = showDimensions ? dimFont * 2.1 : big * 0.006;
  const vbFullW = vbW + margin * 2;
  const vbFullL = vbL + margin * 2;
  /** Meia distancia das marcas de extremidade da cota, em mm de desenho. */
  const dimTick = margin * 0.22;
  /** Eixo da cota: no meio da margem, do lado de fora da chapa. */
  const dimAt = -margin * 0.5;

  const svgRef = useRef<SVGSVGElement | null>(null);
  const [view, setView] = useState<ViewTransform>(IDENTITY);
  const dragRef = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  /** Ponteiros encostados agora: 1 = arrasto, 2 = pinca. */
  const pointersRef = useRef(new Map<number, PointerPos>());
  /** Quadro anterior da pinca, para medir o quanto os dedos abriram. */
  const pinchRef = useRef<PinchState | null>(null);
  /** Houve pinca neste gesto: o toque final nao pode virar clique em peca. */
  const pinchedRef = useRef(false);

  // Cada instancia precisa de ids proprios: varios diagramas coexistem na pagina
  // (impressao de todas as chapas) e `url(#id)` duplicado quebraria as hachuras.
  const uid = safeId(useId());
  const wasteId = `pedra-waste-${uid}`;
  const offcutId = `pedra-offcut-${uid}`;

  // Ao trocar de chapa a vista volta ao enquadramento inteiro.
  useEffect(() => {
    setView(IDENTITY);
  }, [sheet.index]);

  /**
   * Pixel de tela -> unidade do viewBox (mm). O viewBox comeca em
   * (-margin, -margin): sem descontar a margem o zoom "puxa" para o canto.
   */
  const toViewBox = useCallback(
    (rect: DOMRect, clientX: number, clientY: number): { x: number; y: number } => ({
      x: ((clientX - rect.left) / rect.width) * vbFullW - margin,
      y: ((clientY - rect.top) / rect.height) * vbFullL - margin,
    }),
    [vbFullW, vbFullL, margin],
  );

  /**
   * Zoom mantendo fixo o ponto do desenho que esta sob o cursor / sob o meio
   * dos dedos. `from` e `to` sao esse ponto de ancoragem em unidades do viewBox
   * ANTES e DEPOIS do gesto — na roda do mouse sao o mesmo ponto; na pinca,
   * mover os dois dedos juntos tambem arrasta a chapa.
   */
  const zoomAround = useCallback(
    (factor: number, from: { x: number; y: number }, to: { x: number; y: number }): void => {
      setView((prev) => {
        const next = clamp(prev.scale * factor, MIN_SCALE, MAX_SCALE);
        if (next === prev.scale && from.x === to.x && from.y === to.y) return prev;
        const px = (from.x - prev.tx) / prev.scale;
        const py = (from.y - prev.ty) / prev.scale;
        return clampView({ scale: next, tx: to.x - px * next, ty: to.y - py * next }, vbW, vbL);
      });
    },
    [vbW, vbL],
  );

  // Roda do mouse = zoom no ponteiro. Listener nativo porque o React entrega
  // `wheel` como passivo e `preventDefault()` seria ignorado.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const onWheel = (ev: WheelEvent): void => {
      ev.preventDefault();
      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const at = toViewBox(rect, ev.clientX, ev.clientY);
      const delta = ev.deltaMode === 1 ? ev.deltaY * 16 : ev.deltaY;
      zoomAround(Math.exp(-delta * 0.0015), at, at);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [toViewBox, zoomAround]);

  const handlePointerDown = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    // O teste de botao so vale para mouse: num toque `button` e sempre 0, mas
    // num `pointerType` de caneta com botao lateral poderia nao ser.
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 1) return;
    const pointers = pointersRef.current;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture?.(e.pointerId);

    if (pointers.size === 1) {
      dragRef.current = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
      pinchRef.current = null;
      return;
    }
    // Chegou o segundo dedo: o arrasto acaba e a pinca comeca.
    dragRef.current = null;
    pinchRef.current = readPinch(pointers);
    pinchedRef.current = true;
  }, []);

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<SVGSVGElement>) => {
      const pointers = pointersRef.current;
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      const rect = e.currentTarget.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      // --- dois dedos: pinca (zoom + arrasto ao mesmo tempo) -----------------
      if (pointers.size >= 2) {
        const now = readPinch(pointers);
        const before = pinchRef.current;
        pinchRef.current = now;
        if (now === null || before === null) return;
        zoomAround(
          now.dist / before.dist,
          toViewBox(rect, before.cx, before.cy),
          toViewBox(rect, now.cx, now.cy),
        );
        return;
      }

      // --- um dedo / mouse: arrasto -----------------------------------------
      const drag = dragRef.current;
      if (!drag || drag.id !== e.pointerId) return;
      const dx = e.clientX - drag.x;
      const dy = e.clientY - drag.y;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
      drag.moved = true;
      drag.x = e.clientX;
      drag.y = e.clientY;
      // Deslocamento: escala pelo viewBox INTEIRO (a margem tambem ocupa px na
      // tela). Como e uma diferenca, a origem negativa nao entra na conta.
      const ux = (dx / rect.width) * vbFullW;
      const uy = (dy / rect.height) * vbFullL;
      setView((prev) => clampView({ scale: prev.scale, tx: prev.tx + ux, ty: prev.ty + uy }, vbW, vbL));
    },
    [vbW, vbL, vbFullW, vbFullL, toViewBox, zoomAround],
  );

  const handlePointerUp = useCallback((e: ReactPointerEvent<SVGSVGElement>) => {
    const pointers = pointersRef.current;
    if (!pointers.delete(e.pointerId)) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);

    const drag = dragRef.current;
    const dragged = drag !== null && drag.id === e.pointerId && drag.moved;
    if (drag !== null && drag.id === e.pointerId) dragRef.current = null;

    // Sobrou um dedo depois da pinca: ele reassume o arrasto a partir de onde
    // esta. Sem reancorar aqui, o proximo movimento seria medido contra a
    // posicao do dedo que saiu e a chapa daria um salto.
    pinchRef.current = pointers.size >= 2 ? readPinch(pointers) : null;
    if (pointers.size === 1 && dragRef.current === null) {
      const [entry] = [...pointers.entries()];
      if (entry !== undefined) {
        const [id, pos] = entry;
        dragRef.current = { id, x: pos.x, y: pos.y, moved: true };
      }
    }

    // Arrastou ou deu pinca: o toque nao seleciona a peca que estava embaixo.
    if (dragged || pinchedRef.current) {
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
    if (pointers.size === 0) pinchedRef.current = false;
  }, []);

  const handlePartClick = useCallback(
    (partId: Id) => {
      if (suppressClickRef.current) return;
      onClickPart?.(partId);
    },
    [onClickPart],
  );

  const handleLeave = useCallback(() => {
    onHoverPart?.(null);
  }, [onHoverPart]);

  const width = Math.max(80, Math.round(maxWidth));
  // A proporcao do elemento segue o viewBox COM a margem: usar vbL/vbW deixaria
  // a moldura fora do aspecto e o `preserveAspectRatio` comeria as cotas.
  const height = Math.max(60, Math.round((width * vbFullL) / vbFullW));
  const wrapperStyle: CSSProperties = { width: `${width}px`, height: `${height}px` };

  const cutsWithNumbers = sheet.cuts.length <= 30;
  const dimsText = useMemo(
    () =>
      formatDimensions(
        sheet.length,
        sheet.width,
        settings.unit,
        settings.decimalPlaces,
        settings.dimensionOrder,
      ),
    [sheet.length, sheet.width, settings.unit, settings.decimalPlaces, settings.dimensionOrder],
  );

  const zoomed = view.scale !== 1 || view.tx !== 0 || view.ty !== 0;

  return (
    <div className="diagram-root" style={wrapperStyle}>
      <svg
        ref={svgRef}
        className={highlightedPartId ? 'diagram-svg has-highlight' : 'diagram-svg'}
        viewBox={`${-margin} ${-margin} ${vbFullW} ${vbFullL}`}
        width={width}
        height={height}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Diagrama da chapa ${sheet.index + 1}, ${dimsText}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onMouseLeave={handleLeave}
        onDoubleClick={() => setView(IDENTITY)}
      >
        <title>{`Chapa ${sheet.index + 1} — ${sheet.label || 'sem nome'} — ${dimsText}`}</title>
        <desc>
          {`${sheet.placements.length} peças posicionadas, ${sheet.numCuts} cortes, ` +
            `${sheet.offcuts.length} áreas livres.`}
        </desc>

        <defs>
          <pattern id={wasteId} patternUnits="userSpaceOnUse" width={hatch} height={hatch}>
            <path className="diagram-hatch-waste" d={`M0 ${hatch} L ${hatch} 0`} />
            <path
              className="diagram-hatch-waste"
              d={`M${-hatch / 4} ${hatch / 4} L ${hatch / 4} ${-hatch / 4}`}
            />
            <path
              className="diagram-hatch-waste"
              d={`M${(hatch * 3) / 4} ${(hatch * 5) / 4} L ${(hatch * 5) / 4} ${(hatch * 3) / 4}`}
            />
          </pattern>
          <pattern id={offcutId} patternUnits="userSpaceOnUse" width={hatch * 1.8} height={hatch * 1.8}>
            <path className="diagram-hatch-offcut" d={`M0 ${hatch * 1.8} L ${hatch * 1.8} 0`} />
          </pattern>
        </defs>

        <g transform={`translate(${view.tx} ${view.ty}) scale(${view.scale})`}>
          <rect className="diagram-sheet" x={0} y={0} width={vbW} height={vbL} />

          <OffcutLayer
            offcuts={sheet.offcuts}
            wasteFill={`url(#${wasteId})`}
            offcutFill={`url(#${offcutId})`}
            showLabels={showLabels}
            settings={settings}
            baseFont={baseFont}
          />

          <g className="diagram-parts">
            {sheet.placements.map((p, i) => (
              <PartRect
                key={`${p.partId}#${p.instance}`}
                placement={p}
                index={i}
                colorFilled={colorFilled}
                showLabels={showLabels}
                showEdgeBanding={showEdgeBanding}
                edgeBands={edgeBands}
                settings={settings}
                baseFont={baseFont}
                bandMin={bandMin}
                highlighted={highlightedPartId === p.partId}
                onHover={onHoverPart}
                onClick={handlePartClick}
              />
            ))}
          </g>

          {showCuts ? (
            <CutLayer
              cuts={sheet.cuts}
              highlightedCutSeq={highlightedCutSeq ?? null}
              baseFont={baseFont}
              showNumbers={cutsWithNumbers}
            />
          ) : null}

          <rect className="diagram-sheet-border" x={0} y={0} width={vbW} height={vbL} />

          {showDimensions ? (
            <g className="diagram-dims" aria-hidden="true">
              {/* `to` em mm de desenho; `text` formatado do Micron original.
                  `at` e negativo: a cota mora na margem, fora da chapa. */}
              <DimLine
                orientation="h"
                at={dimAt}
                from={0}
                to={vbW}
                text={fromMicronTrim(sheet.width, settings.unit, settings.decimalPlaces)}
                fontSize={dimFont}
                tick={dimTick}
              />
              <DimLine
                orientation="v"
                at={dimAt}
                from={0}
                to={vbL}
                text={fromMicronTrim(sheet.length, settings.unit, settings.decimalPlaces)}
                fontSize={dimFont}
                tick={dimTick}
              />
            </g>
          ) : null}
        </g>
      </svg>

      {zoomed ? (
        <button
          type="button"
          className="btn btn--sm diagram-reset"
          onClick={() => setView(IDENTITY)}
          title="Voltar ao enquadramento da chapa inteira"
        >
          {`Redefinir vista (${Math.round(view.scale * 100)}%)`}
        </button>
      ) : null}
    </div>
  );
}

export default SheetDiagram;
